import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import {
  ArrowRight, Check, Loader2, Lock, Percent, Plus, Trash2, Wallet,
} from "lucide-react";
import { FullFrameLogo, ffGradient } from "./FullFrameLogo";
import { UploadButtons } from "./UploadButtons";
import { PaperlessSend } from "./PaperlessSend";
import {
  resolveCompanyIdBySlug,
  listCompanyExpenses, uploadCompanyExpense, updateCompanyExpense, deleteCompanyExpense,
  getFullFrameConfig,
} from "./api";

const ils = (n) => new Intl.NumberFormat("he-IL", { style: "currency", currency: "ILS", maximumFractionDigits: 2 }).format(Number(n) || 0);
const monthOf = (d) => (d || "").slice(0, 7);
const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/**
 * /:slug/expenses — Company-wide expenses (NOT per-production).
 *
 * Two sections:
 *   1. הוצאות חד פעמיות — counted ONLY in the month of the receipt's `date`.
 *   2. הוצאות שותפות (recurring) — auto-applied every month between
 *      `start_month` and `end_month`. Locking an expense for "all future
 *      months" simply leaves `end_month` empty (open-ended).
 *
 * Both feed into the dashboard's monthly summary (expenses + VAT credit).
 */
export default function FullFrameCompanyExpenses() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState(null);
  const [paperlessPhone, setPaperlessPhone] = useState("972437488824");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingKind, setUploadingKind] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const cid = await resolveCompanyIdBySlug(slug);
        setCompanyId(cid);
        const [items, cfg] = await Promise.all([
          listCompanyExpenses(cid),
          getFullFrameConfig().catch(() => null),
        ]);
        setRows(items);
        if (cfg?.paperless_wa_phone) setPaperlessPhone(cfg.paperless_wa_phone);
      } catch (e) {
        toast.error(e.response?.data?.detail || "שגיאה בטעינה");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const refresh = async () => setRows(await listCompanyExpenses(companyId));

  const onPick = async (kind, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("יש לבחור תמונה"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("גודל מקסימלי: 8MB"); return; }
    setUploadingKind(kind);
    try {
      const row = await uploadCompanyExpense({
        file, kind, companyId,
        startMonth: kind === "recurring" ? currentMonth() : "",
        endMonth: "",
      });
      if (row.ocr_status === "done") toast.success(`זוהה: ${row.business_name || "ללא שם"}`);
      else if (row.ocr_status === "failed") toast.warning("התמונה נשמרה — מלא ידנית");
      else toast.success("נוסף");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בהעלאה");
    } finally { setUploadingKind(null); }
  };

  // Insert an EMPTY row (no image) the user can fill in by hand.
  const onManual = async (kind) => {
    setUploadingKind(kind);
    try {
      await uploadCompanyExpense({
        file: null, kind, companyId,
        startMonth: kind === "recurring" ? currentMonth() : "",
        endMonth: "",
      });
      toast.success("נוספה שורה ריקה");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בהוספה");
    } finally { setUploadingKind(null); }
  };

  const onUpdate = async (rowId, patch) => {
    const payload = {};
    if ("business_name" in patch) payload.business_name = patch.business_name || "";
    if ("business_phone" in patch) payload.business_phone = patch.business_phone || "";
    if ("product" in patch) payload.product = patch.product || "";
    if ("pre_vat" in patch) payload.pre_vat = Number(patch.pre_vat) || 0;
    if ("total_with_vat" in patch) payload.total_with_vat = Number(patch.total_with_vat) || 0;
    if ("date" in patch) payload.date = patch.date || "";
    if ("start_month" in patch) payload.start_month = patch.start_month || "";
    if ("end_month" in patch) payload.end_month = patch.end_month || "";
    // Optimistically patch the row in local state so the UI doesn't lose
    // focus / unmount during the network roundtrip (the user complained the
    // values disappeared — that was because refresh() replaced the array).
    setRows((arr) => arr.map((r) => (r.id === rowId ? { ...r, ...payload } : r)));
    try {
      await updateCompanyExpense(rowId, payload, companyId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה");
    }
  };

  const onDelete = async (rowId) => {
    if (!window.confirm("למחוק את ההוצאה?")) return;
    try { await deleteCompanyExpense(rowId, companyId); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "שגיאה"); }
  };

  const oneTime = rows.filter((r) => r.kind === "one_time");
  const recurring = rows.filter((r) => r.kind === "recurring");

  return (
    <div className="min-h-screen bg-stone-800 text-stone-100" dir="rtl">
      <Toaster position="top-center" richColors dir="rtl" theme="dark" />

      {/* Solid uniform grey backdrop — no decorative haloes per user request. */}

      <header className="relative z-10 border-b border-white/10 bg-stone-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to={`/${slug}`} className="inline-flex items-center gap-1.5 text-stone-400 hover:text-white transition" data-testid="ce-back-link">
            <ArrowRight className="h-4 w-4" />
            <span className="text-sm">דשבורד</span>
          </Link>
          <FullFrameLogo size="sm" />
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-10">
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-widest text-stone-400 font-bold">הוצאות החברה</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold flex items-center gap-3 text-white">
            <Wallet className="h-7 w-7 text-pink-400" />
            הוצאות כלליות
          </h1>
          <p className="text-stone-400 text-sm">הוצאות שלא משויכות להפקה — אך משפיעות על הסיכום החודשי בדשבורד.</p>
        </div>

        {/* One-time expenses */}
        <ExpensesBlock
          title="הוצאות חד פעמיות"
          subtitle="הוצאה שמופיעה רק בחודש שלה"
          rows={oneTime}
          loading={loading}
          uploadingKind={uploadingKind}
          paperlessPhone={paperlessPhone}
          onPick={(file) => onPick("one_time", file)}
          onManual={() => onManual("one_time")}
          onUpdate={onUpdate}
          onDelete={onDelete}
          kind="one_time"
        />

        {/* Recurring expenses */}
        <ExpensesBlock
          title="הוצאות שותפות (קבועות)"
          subtitle="הוצאה שחוזרת כל חודש (משרד, מנוי, ביטוח). תיכלל אוטומטית בחישובי החודש."
          rows={recurring}
          loading={loading}
          uploadingKind={uploadingKind}
          paperlessPhone={paperlessPhone}
          onPick={(file) => onPick("recurring", file)}
          onManual={() => onManual("recurring")}
          onUpdate={onUpdate}
          onDelete={onDelete}
          kind="recurring"
        />
      </main>
    </div>
  );
}

function ExpensesBlock({ title, subtitle, rows, loading, uploadingKind, paperlessPhone, onPick, onManual, onUpdate, onDelete, kind }) {
  const busy = uploadingKind === kind;
  // Number of columns differs slightly between one_time and recurring; keep
  // empty-state colSpan in sync.
  const colCount = kind === "one_time" ? 8 : 9;

  return (
    <section className="space-y-3" data-testid={`ce-block-${kind}`}>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h2 className="font-heading text-2xl font-bold">{title}</h2>
          <p className="text-xs text-stone-400 mt-0.5">{subtitle}</p>
        </div>
        <UploadButtons
          onPick={onPick}
          onManual={onManual}
          busy={busy}
          idPrefix={`ce-${kind}`}
          testidPrefix={`ce-${kind}`}
        />
      </div>

      <div className="rounded-2xl border border-white/10 bg-stone-700 overflow-x-auto">
        <table className="w-full text-base min-w-[1380px]" data-testid={`ce-table-${kind}`}>
          <thead className="bg-white/[0.04] text-xs uppercase tracking-wider text-stone-300">
            <tr>
              <th className="text-right px-3 py-3.5 font-semibold w-20">תמונה</th>
              <th className="text-right px-3 py-3.5 font-semibold min-w-[180px]">שם העסק</th>
              <th className="text-right px-3 py-3.5 font-semibold min-w-[140px]">מוצר/שירות</th>
              <th className="text-right px-3 py-3.5 font-semibold w-32">סכום</th>
              <th className="text-right px-3 py-3.5 font-semibold w-56 min-w-[224px] text-rose-200 text-base">סה״כ + מע״מ</th>
              {kind === "one_time" && <th className="text-right px-3 py-3.5 font-semibold w-36">תאריך</th>}
              {kind === "recurring" && <th className="text-right px-3 py-3.5 font-semibold w-32">החל מ</th>}
              {kind === "recurring" && <th className="text-right px-3 py-3.5 font-semibold w-40">עד (ריק = ללא הגבלה)</th>}
              <th className="text-right px-3 py-3.5 font-semibold w-40">פפרלס</th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading ? (
              <tr><td colSpan={colCount} className="px-4 py-12 text-center text-stone-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={colCount} className="px-4 py-12 text-center text-stone-400">אין שורות.</td></tr>
            ) : (
              rows.map((r) => (
                <CompanyExpenseRow key={r.id} row={r} kind={kind} paperlessPhone={paperlessPhone} onUpdate={(patch) => onUpdate(r.id, patch)} onDelete={() => onDelete(r.id)} />
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CompanyExpenseRow({ row, kind, paperlessPhone, onUpdate, onDelete }) {
  const [draft, setDraft] = useState({ ...row });
  // Track which fields the user touched since the last save so we only push
  // changed values. The patchTimer batches keystrokes — 500ms after the user
  // stops typing we POST whatever changed. This gives true auto-save UX
  // (the user complained that onBlur missed values when the page was
  // refreshed before clicking outside).
  const dirtyRef = useRef({});
  const patchTimer = useRef(null);
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  useEffect(() => {
    setDraft({ ...row });
    dirtyRef.current = {};
  }, [row.id]);

  const u = (k, v) => {
    setDraft((d) => ({ ...d, [k]: v }));
    dirtyRef.current[k] = v;
    if (patchTimer.current) clearTimeout(patchTimer.current);
    patchTimer.current = setTimeout(async () => {
      const patch = { ...dirtyRef.current };
      if (Object.keys(patch).length === 0) return;
      dirtyRef.current = {};
      setSaving(true);
      try {
        await onUpdate(patch);
        setSavedTick(true);
        // Hide the "saved" tick after 1.2s.
        setTimeout(() => setSavedTick(false), 1200);
      } finally {
        setSaving(false);
      }
    }, 500);
  };

  // For paste/programmatic events (e.g. "+18%" button) we need a way to
  // flush immediately without waiting for the debounce.
  const flushNow = (patch) => {
    if (patchTimer.current) clearTimeout(patchTimer.current);
    dirtyRef.current = { ...dirtyRef.current, ...patch };
    const out = { ...dirtyRef.current };
    dirtyRef.current = {};
    onUpdate(out);
  };

  // Plain wa.me deeplink — NO ?text= prefill. Chat opens empty so the user
  // pastes the previously-copied image and sends it cleanly.
  const waHref = paperlessPhone ? `https://wa.me/${paperlessPhone}` : "#";

  return (
    <tr data-testid={`ce-row-${row.id}`} className="hover:bg-white/[0.03]">
      <td className="px-2 py-2.5 relative">
        {/* Tiny auto-save indicator overlays the first cell — uses absolute
            positioning to avoid adding an extra column that would offset
            every other row. */}
        {(saving || savedTick) && (
          <div className="absolute top-1 left-1 z-10 pointer-events-none">
            {saving ? (
              <Loader2 className="h-3 w-3 animate-spin text-pink-400" />
            ) : (
              <Check className="h-3 w-3 text-emerald-400" />
            )}
          </div>
        )}
        {row.image_url ? (
          <a href={row.image_url} target="_blank" rel="noreferrer">
            <img src={row.image_url} alt="חשבונית" className="h-14 w-14 rounded-lg object-cover border border-white/10 hover:border-pink-400/60 transition" />
          </a>
        ) : (
          <div className="h-14 w-14 rounded-lg bg-stone-800 border border-white/10" />
        )}
      </td>
      <td className="px-3 py-2.5">
        <input type="text" value={draft.business_name || ""} onChange={(e) => u("business_name", e.target.value)}
          placeholder="שם העסק"
          className="w-full bg-stone-700 border border-white/15 text-white font-semibold placeholder:text-stone-300 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 rounded-lg px-3 py-2 text-base"
          data-testid={`ce-name-${row.id}`} />
      </td>
      <td className="px-3 py-2.5">
        <input type="text" value={draft.product || ""} onChange={(e) => u("product", e.target.value)}
          placeholder="מוצר/שירות"
          className="w-full bg-stone-700 border border-white/15 text-white font-semibold placeholder:text-stone-300 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 rounded-lg px-3 py-2 text-base"
          data-testid={`ce-product-${row.id}`} />
      </td>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-1.5">
          <input
            type="number" step="0.01" dir="ltr"
            value={draft.pre_vat ?? 0}
            onChange={(e) => u("pre_vat", e.target.value)}
            placeholder="0"
            className="flex-1 min-w-0 bg-stone-700 border border-white/15 text-white font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 rounded-lg px-3 py-2 text-right tabular-nums text-base"
            data-testid={`ce-prevat-${row.id}`}
          />
          {/* + 18% VAT auto-calc — same height as the input, no overlap.
              Hidden when the row already has total_with_vat set. */}
          {Number(draft.pre_vat) > 0 && Number(draft.total_with_vat) === 0 && (
            <button
              type="button"
              onClick={() => {
                const pre = Number(draft.pre_vat) || 0;
                const total = Math.round(pre * 1.18 * 100) / 100;
                setDraft((d) => ({ ...d, total_with_vat: total }));
                flushNow({ pre_vat: pre, total_with_vat: total });
              }}
              className={`shrink-0 inline-flex items-center justify-center h-10 px-2 rounded-lg ${ffGradient} text-white text-[11px] font-extrabold whitespace-nowrap shadow-md shadow-pink-500/20 hover:opacity-90 transition`}
              title="הוסף 18% מע״מ אוטומטית לסה״כ"
              data-testid={`ce-add-vat-${row.id}`}
            >
              +18%
            </button>
          )}
        </div>
      </td>
      <td className="px-3 py-2.5">
        <input type="number" step="0.01" dir="ltr" value={draft.total_with_vat ?? 0} onChange={(e) => u("total_with_vat", e.target.value)}
          className="w-full bg-rose-950 border-2 border-rose-500/70 text-rose-50 font-extrabold focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 rounded-xl px-3 py-3 text-right tabular-nums text-2xl shadow-md shadow-rose-900/40"
          data-testid={`ce-total-${row.id}`} />
      </td>
      {kind === "one_time" && (
        <td className="px-3 py-2.5">
          <input type="date" dir="ltr" value={draft.date || ""} onChange={(e) => u("date", e.target.value)}
            className="w-full bg-stone-700 border border-white/15 text-white font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 rounded-lg px-3 py-2 text-base"
            data-testid={`ce-date-${row.id}`} />
        </td>
      )}
      {kind === "recurring" && (
        <>
          <td className="px-3 py-2.5">
            <input type="month" dir="ltr" value={draft.start_month || ""} onChange={(e) => u("start_month", e.target.value)}
              className="w-full bg-stone-700 border border-white/15 text-white font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 rounded-lg px-3 py-2 text-base [color-scheme:dark]"
              data-testid={`ce-start-${row.id}`} />
          </td>
          <td className="px-3 py-2.5">
            <div className="flex items-center gap-1.5">
              <input type="month" dir="ltr" value={draft.end_month || ""} onChange={(e) => u("end_month", e.target.value)}
                placeholder="ללא הגבלה"
                className="flex-1 min-w-0 bg-stone-700 border border-white/15 text-white font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500/50 rounded-lg px-3 py-2 text-base [color-scheme:dark]"
                data-testid={`ce-end-${row.id}`} />
              {!draft.end_month && (
                <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-bold whitespace-nowrap" title="הוצאה תיכלל בכל חודש מ'החל מ' והלאה">
                  <Lock className="h-3 w-3" />
                  קבוע
                </span>
              )}
            </div>
          </td>
        </>
      )}
      <td className="px-3 py-2.5">
        <PaperlessSend imageUrl={row.image_url} businessName={row.business_name} waHref={waHref} disabled={!paperlessPhone} testIdBase={`ce-paperless-${row.id}`} />
      </td>
      <td className="px-2 py-2.5">
        <button type="button" onClick={onDelete}
          className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 transition rounded-lg p-2"
          data-testid={`ce-delete-${row.id}`} aria-label="מחק">
          <Trash2 className="h-5 w-5" />
        </button>
      </td>
    </tr>
  );
}
